import React from 'react';
import styled from 'styled-components';
import { theme } from '@theme';

interface StatsCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: React.ReactNode;
  color?: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
}

const CardContainer = styled.div`
  background: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg};
  box-shadow: ${theme.shadows.md};
  transition: all ${theme.transitions.normal};

  &:hover {
    box-shadow: ${theme.shadows.lg};
    transform: translateY(-2px);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${theme.spacing.md};
`;

const IconWrapper = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  border-radius: ${theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.color}20;
  color: ${props => props.color};
  font-size: 24px;
`;

const Content = styled.div`
  flex: 1;
`;

const Title = styled.div`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}80;
  margin-bottom: ${theme.spacing.xs};
`;

const Value = styled.div`
  font-size: ${theme.typography.sizes['3xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  line-height: 1;
`;

const Subtitle = styled.div`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.text}60;
  margin-top: ${theme.spacing.xs};
`;

const Trend = styled.div<{ isPositive: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.medium};
  color: ${props => (props.isPositive ? theme.colors.success : theme.colors.error)};
  margin-top: ${theme.spacing.sm};
`;

export const StatsCard: React.FC<StatsCardProps> = ({
  title,
  value,
  subtitle,
  icon,
  color = theme.colors.primary,
  trend,
}) => {
  return (
    <CardContainer>
      <CardHeader>
        <Content>
          <Title>{title}</Title>
          <Value>{value}</Value>
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
          {trend && (
            <Trend isPositive={trend.isPositive}>
              {trend.isPositive ? '↑' : '↓'} {Math.abs(trend.value)}%
            </Trend>
          )}
        </Content>
        {icon && <IconWrapper color={color}>{icon}</IconWrapper>}
      </CardHeader>
    </CardContainer>
  );
};
